import { useApi } from "@platform/api";
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useOutletContext } from "react-router";

import { Baseline, BBEOutletContext, Scenario } from "../pages/BBE/types";

import useToastNotificationService from "./useToastNotificationService";
import { useAddScenarioMutation, useBaselineRenameMutation } from "./useBbe";

type UseScenarioModelActionsResults = {
  renameModel: UseMutationResult<Baseline, Error, string>;
  createModel: UseMutationResult<Baseline, Error>;
  addScenario: UseMutationResult<
    Scenario,
    Error,
    { name: string; description?: string }
  >;
};

export const useScenarioModelActions = (
  modelId: string,
  brokerId: string,
  handleCloseModal: () => void,
): UseScenarioModelActionsResults => {
  const axios = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();
  const { setSelectedModelId } = useOutletContext<BBEOutletContext>();

  const renameModel = useBaselineRenameMutation(modelId, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bbe", "baselines", brokerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["bbe", "scenariosSummary", modelId],
      });
      handleCloseModal();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to rename model. Please try again.",
      });
    },
  });

  const createModel = useMutation({
    mutationFn: (label: string) =>
      axios
        .post<Baseline>(`/bbe/v1/baselines`, {
          sfBrokerId: brokerId,
          label,
        })
        .then((res) => res.data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["bbe", "baselines", brokerId],
      });
      setSelectedModelId(data.id);
      handleCloseModal();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to create model. Please try again.",
      });
    },
  });

  const addScenario = useAddScenarioMutation(modelId, {
    onSuccess: handleCloseModal,
  });

  return {
    renameModel,
    createModel,
    addScenario,
  };
};
